<script lang="ts" setup>
  import ImageSplashSignIn from '~/assets/images/login/splash-sign-in.png'
  import { useAuthStore, useUserStore } from '~/store'
  import type { OnboardingQueries } from '~/types/onboard'
  import GInputField from '~/components/form/GInputField/GInputField.vue'
  import { z } from 'zod'
  import { EMAIL_INVALID_ERROR, PASSWORD_LENGTH_ERROR_MESSAGE } from '~/constants/errorMessage'
  import type { RelishRequest } from '~/composables/relish'
  import { enableMadBunday } from '~/constants/madBunday'
  import { createClientTrackingEventId } from '~/composables/canonicalTracking'

  const props = withDefaults(
    defineProps<{
      modalId?: string
      queries?: OnboardingQueries
      hideBackButton?: boolean
      hideSignUpLink?: boolean
      isView?: boolean
      onSuccessfulSignIn?: () => Promise<void>
    }>(),
    {
      modalId: undefined,
      queries: undefined,
      hideBackButton: false,
      hideSignUpLink: false,
      isView: false,
      onSuccessfulSignIn: undefined
    }
  )

  const vueRouter = useRouter()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const { isMobileMode, isWebMode } = useAppMode()
  const fetchRelishData = useFetchRelish()
  const { showJoinRelishModal } = useJoinRelishModal()
  const { showResetPasswordModal } = useResetPasswordModal()
  const { isConnected, clearNetworkListener } = useNetworkStatus()
  const contentEl = useKeyboardOpenAndScroll()
  const { showTwoFactorVerificationModal, twoFactorVerificationModalId } = useTwoFactorAuthenticationModal()

  const emailInputElement = ref<InstanceType<typeof GInputField> | null>(null)
  const errorMessage = ref('')

  const isSubmitDisabled = computed(() => {
    return !meta.value.valid || !isConnected.value || isSubmitting.value
  })

  async function dismissModal() {
    await modalController.dismiss(undefined, undefined, props.modalId)
    await clearNetworkListener()
  }

  async function onSignUpLinkClicked() {
    if (props.isView) {
      await clearNetworkListener()

      await vueRouter.replace({ name: 'sign-up', query: { ...props.queries } })
    } else {
      await dismissModal()
      await showJoinRelishModal({ queries: props.queries || {} })
    }
  }

  const validationSchema = toTypedSchema(
    z.object({
      email: z
        .string()
        .min(6, { message: 'Minimum 6 characters' })
        .max(100, { message: 'Max 100 characters' })
        .email({ message: EMAIL_INVALID_ERROR }),
      password: z.string().min(8, { message: PASSWORD_LENGTH_ERROR_MESSAGE })
    })
  )

  const { handleSubmit, meta, isSubmitting, values } = useForm({
    validationSchema,
    initialValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = handleSubmit(async (values) => {
    let didSignIn = false

    try {
      errorMessage.value = ''
      didSignIn = await doSignIn(values.email, values.password)
    } catch (error: any) {
      errorMessage.value =
        getApiErrorMessage(error) ?? 'Email or Password is not correct. Try again or create an account'
      const errorCode = getApiErrorCode(error)
      // If the error is a 412 error and the error code is 'TWO_FACTOR_AUTHENTICATION_REQUIRED', show the two factor authentication modal
      if (error?.status === 412 && errorCode === 'TWO_FACTOR_AUTHENTICATION_REQUIRED') {
        const deliveryMethod = error?.data?.details?.deliveryMethod
        const message = error?.data?.details?.maskedDestination
        if (deliveryMethod === 'email' || deliveryMethod === 'sms') {
          errorMessage.value = ''
          await showTwoFactorVerificationModal({
            source: 'sign-in',
            title: 'Enter code',
            message,
            showPasswordInput: false,
            transparentModalStyle: true,
            onSubmit: onOtpSubmit,
            onSuccessfulVerifyCode: async () => {
              trackUserSignIn()
              return Promise.resolve()
            },
            onSignOut: () => {},
            onDismiss: () => {}
          })
        }
      }
    } finally {
      if (didSignIn) {
        trackUserSignIn()
      }
    }
  })

  async function doSignIn(email: string, password: string, otpCode?: string): Promise<boolean> {
    let didSignIn = false

    const signInResponse = await signIn({
      emailAddress: email,
      password: password,
      twoFactorAuthenticationCode: otpCode
    })

    if (signInResponse.session) {
      await authStore.signIn(signInResponse.session, signInResponse.user!)
      didSignIn = true

      if (props.onSuccessfulSignIn) {
        await props.onSuccessfulSignIn()
      }

      // dismiss the Two Factor Authentication modal, if showing
      const topModal = await modalController.getTop()
      if (twoFactorVerificationModalId && topModal?.id === twoFactorVerificationModalId) {
        await modalController.dismiss(undefined, undefined, twoFactorVerificationModalId)
      }
      if (isWebMode) {
        emitDidSignInFromWebsite(props.queries?.source === 'marketing-site')

        // Launches a modal if the source is 'checkout'
        if (!props.isView) {
          // fetch mad-bunday data for profile completion
          await fetchMadBunday()
          await dismissModal()
        } else {
          await clearNetworkListener()
          await vueRouter.replace({ name: 'home' })
        }
      } else {
        // fetch mad-bunday data for profile completion
        await fetchMadBunday()
        await dismissModal()
        // If user has not onboarded and unless the source is 'checkout' or 'referral', navigate to the post sign-in route
        if (!userStore.hasOnboarded && !(props.queries?.source === 'checkout')) {
          var postSignInRoute = await getPostSignInRoute(props.queries || {}, undefined)
          if (postSignInRoute) {
            await vueRouter.replace(postSignInRoute)
          }
        }
      }
    }
    return didSignIn
  }

  function trackUserSignIn() {
    const eventId = createClientTrackingEventId('user_sign_in')
    gaEventOnUserSignIn(props.queries?.source || 'unknown', eventId)
    trackingEventOnUserSignIn({ content_name: props.queries?.source || 'unknown', status: true }, eventId)
  }

  async function onOtpSubmit(verificationCode: string) {
    if (!values.email || !values.password) {
      return
    }
    // Call sign in method again with the verification code
    // exceptions will be handled in the modal
    await doSignIn(values.email, values.password, verificationCode)
  }

  async function fetchMadBunday() {
    if (enableMadBunday) {
      const requests: Promise<void>[] = []

      const relishRequests: RelishRequest[] = ['madBundayEntries']

      requests.push(fetchRelishData(relishRequests))

      try {
        await Promise.allSettled(requests)
      } catch (error) {
        console.error('error fetching data', error)
      }
    }
  }

  function handleBackNavigation() {
    gaEventSignInModalNavigateBack()

    if (props.isView) {
      const routeFrom = vueRouter.options.history.state.back as string
      if (routeFrom) {
        vueRouter.back()
      }
    } else {
      dismissModal()
    }
  }

  async function resetPassword() {
    await clearNetworkListener()

    if (props.isView) {
      await vueRouter.push({ name: 'reset-password' })
    } else {
      await showResetPasswordModal(props.queries)
    }
  }

  watch(
    () => isConnected.value,
    () => {
      if (!isConnected.value) {
        errorMessage.value = 'No Internet Connection'
      } else {
        errorMessage.value = ''
      }
    }
  )

  onMounted(() => {
    const focusOnInputFieldMs = isWebMode ? 1000 : 500

    setTimeout(() => {
      if (!emailInputElement.value) {
        return
      }
      emailInputElement.value.$el.setFocus()
    }, focusOnInputFieldMs)
  })
</script>

<template>
  <GPageHeader
    :back-button-func="handleBackNavigation"
    :hide-back-button="hideBackButton"
    compact
    disable-box-shadow
    title="Welcome Back"
  />

  <ion-content ref="contentEl" class="ion-padding-horizontal">
    <div class="content-container">
      <div class="hero-section">
        <img :src="ImageSplashSignIn" alt="Sign in" class="hero-image" />
      </div>

      <div v-if="errorMessage" class="notification-wrapper">
        <GNotification :message="errorMessage" is-error />
      </div>

      <form id="sign-in-form" @submit.prevent.stop="onSubmit">
        <GInputField ref="emailInputElement" :max-length="100" label="Your email" name="email" type="email" />
        <GInputField label="Password" name="password" type="password" />
      </form>

      <div class="forgotten-password-container mb-8">
        <a @click="resetPassword" @keydown.enter="resetPassword">Forgot Password?</a>
      </div>

      <div v-if="isWebMode">
        <GButton
          :disabled="isSubmitDisabled"
          :label="isSubmitting ? 'Signing in' : 'Sign in'"
          :should-show-loading-animation="isSubmitting"
          animation-color="light"
          block
          @click="onSubmit"
        />
        <div v-if="!hideSignUpLink" class="sign-up-link">
          <span class="body">Don't have an account?</span>
          <a @click="onSignUpLinkClicked" @keydown="onSignUpLinkClicked">Create account</a>
        </div>
      </div>
    </div>
  </ion-content>

  <ion-footer v-if="isMobileMode">
    <ion-toolbar>
      <GButton
        :disabled="isSubmitDisabled"
        :label="isSubmitting ? 'Signing in' : 'Sign in'"
        :should-show-loading-animation="isSubmitting"
        animation-color="light"
        block
        @click="onSubmit"
      />

      <div v-if="!hideSignUpLink" class="sign-up-link">
        <span class="body">Don't have an account?</span>
        <a @click="onSignUpLinkClicked" @keydown="onSignUpLinkClicked">Create account</a>
      </div>
    </ion-toolbar>
  </ion-footer>
</template>

<style lang="scss" scoped>
  .content-container {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .hero-section {
      .hero-image {
        width: 100%;
        border-radius: 32px;
      }
    }

    #sign-in-form {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    a {
      text-decoration: none;
    }

    .forgotten-password-container {
      display: flex;
      flex-direction: row-reverse;
    }
  }

  .sign-up-link {
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding-top: 8px;
  }

  ion-footer ion-toolbar {
    --padding-top: 0px;
    --padding-start: 8px;
    --padding-bottom: 30px;
    --padding-end: 8px;
  }
</style>
